/**
 * ContatoPage.tsx — Página "Falar com o Grupo" (/contato)
 * Adaptada ao Claude Design (light + acentos escuros).
 * Formulário via mailto real + canais diretos.
 *
 * ORB-SITE-002: animações via data-reveal gerenciadas pelo useNectarisFx em SiteLayout.
 */

import { type ChangeEvent, type SyntheticEvent, useState } from "react";
import { NectarisLogoMark } from "../atoms";

/* Formulário de contato */
const CONTACT_EMAIL = "jandir.frjr@gmail.com";

type FormStatus = "idle" | "sending" | "sent";

interface FormFields {
  nome: string;
  empresa: string;
  email: string;
  mensagem: string;
}

function ContactFormLight() {
  const [fields, setFields] = useState<FormFields>({ nome: "", empresa: "", email: "", mensagem: "" });
  const [status, setStatus] = useState<FormStatus>("idle");

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!fields.nome || !fields.email || !fields.mensagem) return;
    setStatus("sending");
    const subject = encodeURIComponent(`Contato via site Nectaris — ${fields.empresa || fields.nome}`);
    const body = encodeURIComponent(
      `Nome: ${fields.nome}\nEmpresa: ${fields.empresa}\nE-mail: ${fields.email}\n\n${fields.mensagem}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setStatus("sent");
  }

  function handleReset() {
    setStatus("idle");
    setFields({ nome: "", empresa: "", email: "", mensagem: "" });
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 0",
    borderBottom: "1px solid #E2E8ED",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    background: "transparent",
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    color: "#0B1B2B",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "IBM Plex Mono, monospace",
    fontSize: "11px",
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    color: "#117E99",
    display: "block",
    marginBottom: "4px",
  };

  if (status === "sent") {
    return (
      <div role="status" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <p style={{ fontFamily: "Space Grotesk, sans-serif", fontWeight: 700, fontSize: "20px", color: "#0B1B2B" }}>
          ✓ Mensagem enviada
        </p>
        <p style={{ fontSize: "15px", color: "#45525C" }}>
          Se o cliente de e-mail não abriu, escreva diretamente para{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "#117E99" }}>{CONTACT_EMAIL}</a>
        </p>
        <button
          type="button"
          onClick={handleReset}
          style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontWeight: 700,
            fontSize: "15px",
            background: "#E0A43B",
            color: "#0B1B2B",
            border: "none",
            borderRadius: "10px",
            padding: "12px 24px",
            cursor: "pointer",
            alignSelf: "flex-start",
          }}
        >
          Nova mensagem
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Formulário de contato com o Nectaris Group"
      noValidate
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      <div>
        <label htmlFor="cf-nome" style={labelStyle}>Nome *</label>
        <input
          id="cf-nome"
          type="text"
          name="nome"
          value={fields.nome}
          onChange={handleChange}
          required
          aria-required="true"
          autoComplete="name"
          placeholder="Seu nome completo"
          style={inputStyle}
        />
      </div>
      <div>
        <label htmlFor="cf-empresa" style={labelStyle}>Empresa</label>
        <input
          id="cf-empresa"
          type="text"
          name="empresa"
          value={fields.empresa}
          onChange={handleChange}
          autoComplete="organization"
          placeholder="Nome da organização"
          style={inputStyle}
        />
      </div>
      <div>
        <label htmlFor="cf-email" style={labelStyle}>E-mail *</label>
        <input
          id="cf-email"
          type="email"
          name="email"
          value={fields.email}
          onChange={handleChange}
          required
          aria-required="true"
          autoComplete="email"
          placeholder="voce@empresa.com"
          style={inputStyle}
        />
      </div>
      <div>
        <label htmlFor="cf-mensagem" style={labelStyle}>Mensagem *</label>
        <textarea
          id="cf-mensagem"
          name="mensagem"
          value={fields.mensagem}
          onChange={handleChange}
          required
          aria-required="true"
          rows={4}
          placeholder="Descreva brevemente sua necessidade..."
          style={{ ...inputStyle, resize: "vertical" }}
        />
      </div>
      <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
        <button
          type="submit"
          disabled={status === "sending"}
          aria-busy={status === "sending"}
          className="ns-btn-primary"
          style={{ fontSize: "15px", padding: "14px 28px" }}
        >
          {status === "sending" ? "Enviando..." : "Agendar diagnóstico"}
        </button>
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="ns-btn-ghost"
          style={{ fontSize: "15px", padding: "14px 28px", borderColor: "#E2E8ED", color: "#45525C" }}
        >
          E-mail direto
        </a>
      </div>
    </form>
  );
}

function ContatoHeader() {
  return (
    <header
      className="ns-grupo-header"
      aria-labelledby="contato-h1"
      style={{ background: "#0B1B2B" }}
    >
      <div className="ns-wrap">
        <p className="ns-eyebrow ns-eyebrow--dark">contato</p>
        <h1 id="contato-h1" style={{ color: "#FFFFFF", maxWidth: "22ch" }}>
          Fale com o{" "}
          <span className="ns-accent-dark">Nectaris Group.</span>
        </h1>
        <p style={{ fontSize: "20px", lineHeight: 1.6, color: "rgba(230,239,244,.8)", maxWidth: "54ch", marginTop: "26px" }}>
          Sem formulário genérico. Uma conversa direta sobre o que você precisa.
          Diagnóstico honesto, sem compromisso de contratação imediata.
        </p>
      </div>
    </header>
  );
}

function ContatoCanais() {
  return (
    <section style={{ background: "#F4F6F8", padding: "80px 0", borderBottom: "1px solid #E2E8ED" }} aria-label="Canais de contato">
      <div className="ns-wrap">
        <p className="ns-eyebrow">canais diretos</p>
        <h2 data-reveal style={{ marginBottom: "40px" }}>Fale pela via que preferir</h2>

        <div
          data-reveal
          data-reveal-delay="100"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}
        >
          {[
            { icon: "✉", name: "E-mail", value: "jandir.frjr@gmail.com", href: "mailto:jandir.frjr@gmail.com", external: false },
            { icon: "📱", name: "WhatsApp", value: "+55 (27) 99929-3136", href: "https://wa.me/5527999293136", external: true },
            { icon: "🔗", name: "LinkedIn", value: "Jandir Fraga Junior", href: "https://www.linkedin.com/in/jandir-fraga-junior-msc-0124219a", external: true },
          ].map((canal) => (
            <div
              key={canal.name}
              style={{
                background: "#FFFFFF",
                border: "1px solid #E2E8ED",
                borderRadius: "16px",
                padding: "28px 26px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "24px" }} aria-hidden="true">{canal.icon}</span>
              <p style={{
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "12px",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#B8842D",
                margin: 0,
              }}>
                {canal.name}
              </p>
              <a
                href={canal.href}
                style={{ color: "#117E99", fontSize: "16px", fontWeight: 600 }}
                {...(canal.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                aria-label={`${canal.name}: ${canal.value}`}
              >
                {canal.value}
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContatoFormSection() {
  return (
    <section
      style={{ background: "#FFFFFF", padding: "100px 0", borderBottom: "1px solid #E2E8ED" }}
      aria-labelledby="form-h2"
    >
      <div className="ns-wrap">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "start" }}>
          <div data-reveal>
            <p className="ns-eyebrow">agendar diagnóstico</p>
            <h2 id="form-h2" style={{ marginBottom: "20px" }}>
              Preencha e retornamos em breve
            </h2>
            <p style={{ fontSize: "18px", lineHeight: 1.65, color: "#2A3944", marginBottom: "28px" }}>
              Nos conte sobre sua operação. Sem compromisso de contratação — a primeira
              conversa é sempre um diagnóstico honesto.
            </p>
            <div style={{
              padding: "20px 24px",
              background: "#F4F6F8",
              border: "1px solid #E2E8ED",
              borderRadius: "12px",
            }}>
              <p style={{
                fontFamily: "IBM Plex Mono, monospace",
                fontSize: "11px",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#117E99",
                marginBottom: "8px",
              }}>
                Como funciona
              </p>
              <p style={{ fontSize: "14px", color: "#45525C", lineHeight: 1.65, margin: 0 }}>
                O formulário abre seu cliente de e-mail com os dados preenchidos. Confirme
                o envio no seu aplicativo de e-mail.
              </p>
            </div>
          </div>
          <div data-reveal data-reveal-delay="100">
            <ContactFormLight />
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 820px) {
          .contato-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function ContatoFooter() {
  return (
    <section
      style={{
        background: "radial-gradient(1100px 640px at 50% 120%, #17334F 0%, #0B1B2B 60%)",
        color: "#F2F6F8",
      }}
      aria-label="Rodapé"
    >
      <footer className="ns-footer" aria-label="Rodapé Nectaris">
        <div className="ns-footer__inner">
          <a href="/" className="ns-footer__brand" aria-label="Nectaris — início">
            <NectarisLogoMark size={30} variant="dark" />
            <span className="ns-footer__wordmark">NECTARIS</span>
          </a>
          <span className="ns-footer__copy">© 2026 NECTARIS · BRASIL</span>
        </div>
      </footer>
    </section>
  );
}

export function ContatoPage() {
  return (
    <>
      <ContatoHeader />
      <ContatoCanais />
      <ContatoFormSection />
      <ContatoFooter />
    </>
  );
}
